import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { NavBar } from '../components/NavBar';
import { api } from '../lib/api';
import type { Home, Task } from '@home-guard/shared';

type Period = 'all' | 'year' | '3months';

const PERIOD_LABELS: { key: Period; label: string }[] = [
  { key: 'all', label: 'All time' },
  { key: 'year', label: 'This year' },
  { key: '3months', label: 'Last 3 months' },
];

const CATEGORY_LABEL: Record<string, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  roofing: 'Roofing',
  safety: 'Safety',
  general: 'General',
  exterior: 'Exterior',
  pest_control: 'Pest Control',
  appliance_repair: 'Appliance',
};

function filterByPeriod(tasks: Task[], period: Period): Task[] {
  const now = new Date();
  if (period === 'all') return tasks;

  const cutoff = new Date(now);
  if (period === 'year') {
    cutoff.setMonth(0, 1);
    cutoff.setHours(0, 0, 0, 0);
  } else {
    cutoff.setMonth(cutoff.getMonth() - 3);
    cutoff.setHours(0, 0, 0, 0);
  }

  return tasks.filter((t) => {
    if (!t.completed_at) return false;
    return new Date(t.completed_at) >= cutoff;
  });
}

function groupByMonth(tasks: Task[]): { label: string; tasks: Task[] }[] {
  const map = new Map<string, { label: string; tasks: Task[] }>();

  const sorted = [...tasks].sort((a, b) => {
    const da = a.completed_at ?? a.updated_at;
    const db = b.completed_at ?? b.updated_at;
    return db.localeCompare(da);
  });

  for (const task of sorted) {
    const dateStr = task.completed_at ?? task.updated_at;
    const d = new Date(dateStr);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    const existing = map.get(key);
    if (existing) {
      existing.tasks.push(task);
    } else {
      map.set(key, { label, tasks: [task] });
    }
  }

  return Array.from(map.values());
}

function totalCost(tasks: Task[]): number {
  return tasks.reduce((sum, t) => sum + (t.completed_cost ?? 0), 0);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function exportToHtml(tasks: Task[], homeAddress: string, period: string): void {
  const rows = tasks
    .slice()
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
    .map((t) => `
      <tr>
        <td>${t.completed_at ? formatDate(t.completed_at) : '—'}</td>
        <td>${t.title.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</td>
        <td>${(CATEGORY_LABEL[t.category] ?? t.category).replace(/</g, '&lt;')}</td>
        <td>${t.completed_cost !== null ? `$${t.completed_cost.toFixed(2)}` : '—'}</td>
      </tr>
    `)
    .join('');

  const totalSpent = totalCost(tasks);
  const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Home Maintenance History — ${homeAddress}</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111827; padding: 32px; max-width: 860px; margin: 0 auto; }
  header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; border-bottom: 2px solid #1D4ED8; padding-bottom: 16px; }
  .logo { font-size: 22px; font-weight: 800; color: #1D4ED8; }
  .meta { font-size: 13px; color: #6b7280; margin-top: 4px; }
  .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
  .stat { background: #f9fafb; border-radius: 10px; padding: 14px 16px; }
  .stat-value { font-size: 22px; font-weight: 700; color: #1D4ED8; }
  .stat-label { font-size: 12px; color: #6b7280; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; font-size: 13px; }
  thead { background: #1D4ED8; color: #fff; }
  th { padding: 10px 12px; text-align: left; font-weight: 600; }
  td { padding: 10px 12px; border-bottom: 1px solid #e5e7eb; }
  tr:last-child td { border-bottom: none; }
  tr:nth-child(even) { background: #f9fafb; }
  .total-row td { font-weight: 700; border-top: 2px solid #1D4ED8; background: #eff6ff; }
  .footer { margin-top: 24px; font-size: 11px; color: #9ca3af; text-align: center; }
  @media print {
    body { padding: 16px; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
<header>
  <div>
    <div class="logo">🏠 Home Guard</div>
    <div class="meta">Maintenance History Report</div>
    <div class="meta">${homeAddress}</div>
  </div>
  <div style="text-align:right;">
    <div class="meta">Period: ${period}</div>
    <div class="meta">Generated: ${now}</div>
    <div class="meta">${tasks.length} completed task${tasks.length !== 1 ? 's' : ''}</div>
  </div>
</header>

<div class="summary">
  <div class="stat">
    <div class="stat-value">${tasks.length}</div>
    <div class="stat-label">Tasks completed</div>
  </div>
  <div class="stat">
    <div class="stat-value">$${totalSpent.toFixed(2)}</div>
    <div class="stat-label">Total spent</div>
  </div>
  <div class="stat">
    <div class="stat-value">$${tasks.length > 0 ? (totalSpent / tasks.length).toFixed(0) : '0'}</div>
    <div class="stat-label">Average per task</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th>Date completed</th>
      <th>Task</th>
      <th>Category</th>
      <th>Cost</th>
    </tr>
  </thead>
  <tbody>
    ${rows}
    <tr class="total-row">
      <td colspan="3">Total</td>
      <td>$${totalSpent.toFixed(2)}</td>
    </tr>
  </tbody>
</table>

<div class="footer">
  Generated by Home Guard — homeguard.app · Printed ${now}
</div>

<script>window.onload = () => window.print();<\/script>
</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function MaintenanceHistory() {
  const [period, setPeriod] = useState<Period>('year');

  const { data: homesData } = useQuery({
    queryKey: ['homes'],
    queryFn: () => api.get<{ data: Home[] }>('/homes'),
  });

  const homes = homesData?.data ?? [];
  const primaryHome = homes.find((h) => h.is_primary) ?? homes[0];

  const { data: completedData, isLoading } = useQuery({
    queryKey: ['tasks', primaryHome?.id, 'complete'],
    queryFn: () => api.get<{ data: Task[] }>(`/homes/${primaryHome!.id}/tasks?filter=complete`),
    enabled: !!primaryHome,
  });

  const allCompleted = completedData?.data ?? [];
  const filtered = filterByPeriod(allCompleted, period);
  const grouped = groupByMonth(filtered);
  const spent = totalCost(filtered);

  const homeAddress = primaryHome
    ? `${primaryHome.address_line1}, ${primaryHome.city}, ${primaryHome.state}`
    : 'My Home';

  const periodLabel = PERIOD_LABELS.find((p) => p.key === period)?.label ?? '';

  return (
    <div style={{ minHeight: '100dvh', background: '#f9fafb', paddingBottom: 80 }}>
      {/* Header */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '48px 20px 0',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#111827' }}>
            Maintenance History
          </h1>
          {filtered.length > 0 && (
            <button
              onClick={() => exportToHtml(filtered, homeAddress, periodLabel)}
              style={{
                background: '#1D4ED8', color: '#fff', border: 'none',
                borderRadius: 10, padding: '8px 14px',
                fontSize: 13, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <span>🖨</span> Export
            </button>
          )}
        </div>

        {/* Period tabs */}
        <div style={{ display: 'flex', gap: 4 }}>
          {PERIOD_LABELS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              style={{
                padding: '8px 14px',
                borderRadius: '8px 8px 0 0',
                border: 'none',
                background: period === key ? '#1D4ED8' : 'transparent',
                color: period === key ? '#fff' : '#6b7280',
                fontWeight: period === key ? 600 : 500,
                fontSize: 14,
                cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
            <Spinner />
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: '48px 24px', color: '#9ca3af',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <p style={{ margin: 0, fontWeight: 600, color: '#374151', fontSize: 16 }}>
              No completed tasks
            </p>
            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
              Completed tasks will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <div style={{
                background: '#fff', borderRadius: 16, padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#1D4ED8' }}>
                  {filtered.length}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>Tasks completed</p>
              </div>
              <div style={{
                background: '#fff', borderRadius: 16, padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
              }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#16a34a' }}>
                  ${spent.toFixed(0)}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6b7280' }}>Total spent</p>
              </div>
            </div>

            {/* Grouped task list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {grouped.map(({ label, tasks }) => (
                <div key={label}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 8,
                  }}>
                    <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' }}>
                      {label}
                    </h2>
                    <span style={{ fontSize: 13, color: '#6b7280' }}>
                      {tasks.length} task{tasks.length !== 1 ? 's' : ''}
                      {totalCost(tasks) > 0 && ` · $${totalCost(tasks).toFixed(0)}`}
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {tasks.map((task) => (
                      <HistoryCard key={task.id} task={task} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <NavBar />
    </div>
  );
}

function HistoryCard({ task }: { task: Task }) {
  const categoryLabel = CATEGORY_LABEL[task.category] ?? task.category;
  const dateStr = task.completed_at
    ? formatDate(task.completed_at)
    : task.updated_at
    ? formatDate(task.updated_at)
    : '—';

  return (
    <div style={{
      background: '#fff', borderRadius: 14,
      padding: '14px 16px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: '#f0fdf4',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, fontSize: 16,
      }}>
        ✅
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{
          margin: 0, fontSize: 14, fontWeight: 600, color: '#111827',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {task.title}
        </p>
        <div style={{ display: 'flex', gap: 8, marginTop: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>{categoryLabel}</span>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>{dateStr}</span>
        </div>
      </div>
      {task.completed_cost !== null && task.completed_cost > 0 && (
        <span style={{
          fontSize: 14, fontWeight: 700, color: '#374151',
          flexShrink: 0,
        }}>
          ${task.completed_cost.toFixed(0)}
        </span>
      )}
    </div>
  );
}

function Spinner() {
  return (
    <>
      <div style={{
        width: 28, height: 28,
        border: '3px solid #1D4ED8', borderTopColor: 'transparent',
        borderRadius: '50%', animation: 'spin 0.7s linear infinite',
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
